import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import toast from 'react-hot-toast';
import { FaMapMarkerAlt, FaClock, FaMotorcycle, FaShare, FaPhone } from 'react-icons/fa';
import MapContainer from '../components/MapContainer';
import { getVehicleIcon, getVehicleLabel } from '../utils/vehicleIcons';
import { cleanAddress } from '../utils/addressUtils';

const TripTracking = () => {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [trackingActive, setTrackingActive] = useState(false);
  const [riderLocation, setRiderLocation] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchTripData();
    getCurrentLocation();
  }, [id]);
  
  // Poll for trip updates when trip is active (only update rider location, don't reload everything)
  useEffect(() => {
    if (!trip || (trip.status !== 'in_progress' && trip.status !== 'booked')) {
      return;
    }
    
    // Poll for trip updates every 10 seconds to get real-time rider location
    const pollInterval = setInterval(() => {
      updateRiderLocationOnly(); // Only update location, don't reload entire trip data
    }, 10000); // 10 seconds for real-time tracking
    
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?._id, trip?.status]);

  useEffect(() => {
    let interval;
    if (trackingActive && currentLocation) {
      interval = setInterval(() => {
        // Simulate location updates
        updateLocation();
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [trackingActive, currentLocation]);

  // Update only rider location without reloading entire trip (for polling)
  const updateRiderLocationOnly = async () => {
    try {
      // Try as booking ID first (for passengers)
      try {
        const bookingResponse = await api.get(`/api/bookings/${id}`);
        const booking = bookingResponse.data.booking;
        if (booking) {
          const tripData = booking.trip || booking.tripId;
          
          // Only update rider location if available
          if (tripData?.riderLocation && tripData.riderLocation.lat && tripData.riderLocation.lng) {
            setRiderLocation({
              lat: tripData.riderLocation.lat,
              lng: tripData.riderLocation.lng
            });
          }
          return;
        }
      } catch (bookingError) {
        // Not a booking ID, try as trip ID
      }

      // Try as trip ID (for riders)
      try {
        const tripResponse = await api.get(`/api/trips/${id}`);
        const trip = tripResponse.data.trip;
        if (trip && trip.riderLocation && trip.riderLocation.lat && trip.riderLocation.lng) {
          // Only update rider location
          setRiderLocation({
            lat: trip.riderLocation.lat,
            lng: trip.riderLocation.lng
          });
        }
      } catch (tripError) {
        // Silently fail for polling updates
        console.warn('Polling update error:', tripError);
      }
    } catch (error) {
      // Silently fail for polling updates - don't show errors or set loading
      if (error.response?.status !== 429) {
        console.warn('Polling update error:', error);
      }
    }
  };

  const fetchTripData = async () => {
    try {
      setLoading(true);
      // Try as booking ID first (for passengers)
      try {
        const bookingResponse = await api.get(`/api/bookings/${id}`);
        const booking = bookingResponse.data.booking;
        if (booking) {
          setBooking(booking);
          const tripData = booking.trip || booking.tripId;
          setTrip(tripData);
          
          // Update rider location if available
          if (tripData?.riderLocation && tripData.riderLocation.lat && tripData.riderLocation.lng) {
            setRiderLocation({
              lat: tripData.riderLocation.lat,
              lng: tripData.riderLocation.lng
            });
          }
          
          setLoading(false);
          return;
        }
      } catch (bookingError) {
        // Not a booking ID, try as trip ID
        console.log('Not a booking ID, trying as trip ID');
      }

      // Try as trip ID (for riders)
      try {
        const tripResponse = await api.get(`/api/trips/${id}`);
        const trip = tripResponse.data.trip;
        if (trip) {
          setTrip(trip);
          
          // Update rider location if available
          if (trip.riderLocation && trip.riderLocation.lat && trip.riderLocation.lng) {
            setRiderLocation({
              lat: trip.riderLocation.lat,
              lng: trip.riderLocation.lng
            });
          }

          // Fetch bookings for this trip
          try {
            if (user?.role === 'passenger') {
              // For passenger, get their booking for this trip
              const bookingsResponse = await api.get(`/api/bookings/my-bookings?status=accepted`);
              const bookings = bookingsResponse.data?.bookings || bookingsResponse.data || [];
              const myBooking = bookings.find(b => 
                (b.trip?._id === trip._id) || 
                (b.trip === trip._id) || 
                (b.tripId === trip._id)
              );
              if (myBooking) {
                setBooking(myBooking);
              }
            } else {
              // For rider, get all bookings for this trip
              const bookingsResponse = await api.get(`/api/bookings/by-trip/${id}`);
              const bookings = bookingsResponse.data?.bookings || bookingsResponse.data || [];
              if (bookings.length > 0) {
                setBooking(bookings[0]);
              }
            }
          } catch (bookingError) {
            console.warn('Could not fetch booking details:', bookingError);
            // Continue without booking details
          }
          
          setLoading(false);
          return;
        }
      } catch (tripError) {
        console.error('Trip fetch error:', tripError);
        throw tripError;
      }
    } catch (error) {
      console.error('Fetch trip data error:', error);
      const message = error.response?.data?.message || 'Failed to load trip data';
      
      // Don't show error for 429 (rate limit) - just log it
      if (error.response?.status !== 429) {
        toast.error(message);
      }
      
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Unable to get your location');
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser');
    }
  };

  const updateLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error updating location:', error);
        }
      );
    }
  };

  const startTracking = () => {
    setTrackingActive(true);
    toast.success('Live tracking started');
  };

  const stopTracking = () => {
    setTrackingActive(false);
    toast.success('Live tracking stopped');
  };

  const shareLocation = () => {
    if (navigator.share && currentLocation) {
      navigator.share({
        title: 'My Live Location',
        text: `I'm currently at ${currentLocation.lat}, ${currentLocation.lng}`,
        url: window.location.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success('Location link copied to clipboard');
    }
  };

  const callEmergency = () => {
    window.open('tel:100', '_self');
  };

  // Normalize coordinates - handle both array [lng,lat] and object {lat,lng}
  const normalizeCoords = (coords) => {
    if (!coords) return null;
    if (Array.isArray(coords) && coords.length >= 2) {
      return { lat: parseFloat(coords[1]), lng: parseFloat(coords[0]) };
    }
    if (typeof coords === 'object' && coords.lat != null && coords.lng != null) {
      return { lat: parseFloat(coords.lat), lng: parseFloat(coords.lng) };
    }
    return null;
  };

  // Calculate coordinates (safe even if trip is null)
  const originCoords = trip ? normalizeCoords(trip.origin?.coordinates) : null;
  const destCoords = trip ? normalizeCoords(trip.destination?.coordinates) : null;

  // Get vehicle type from trip rider (with fallback to user's vehicleInfo if trip rider not populated)
  const vehicleType = trip?.rider?.vehicleInfo?.type || user?.vehicleInfo?.type || null;
  const vehicleIcon = useMemo(() => getVehicleIcon(vehicleType), [vehicleType]);

  // Memoize markers to prevent unnecessary re-renders - only recalculate when location changes
  const markers = useMemo(() => {
    const markerList = [];
    
    if (originCoords) {
      markerList.push({
        position: originCoords,
        title: 'Pickup Location',
        icon: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
      });
    }
    
    if (destCoords) {
      markerList.push({
        position: destCoords,
        title: 'Destination',
        icon: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
      });
    }

    // Add rider location marker (for passengers to see rider) - use vehicle-specific icon
    if (riderLocation && user?.role === 'passenger') {
      const vehicleLabel = vehicleType ? `Rider (${getVehicleLabel(vehicleType)})` : 'Rider Location';
      markerList.push({
        position: riderLocation,
        title: vehicleLabel,
        icon: vehicleIcon
      });
    }
    
    // For riders, show their location with vehicle-specific icon (prioritize riderLocation over currentLocation)
    if (riderLocation && user?.role === 'rider') {
      const vehicleLabel = vehicleType ? `Your Location (${getVehicleLabel(vehicleType)})` : 'Your Location';
      markerList.push({
        position: riderLocation,
        title: vehicleLabel,
        icon: vehicleIcon
      });
    } else if (currentLocation && user?.role === 'rider') {
      // Fallback to current location if riderLocation not available - still use vehicle icon if available
      const fallbackIcon = vehicleType ? vehicleIcon : 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png';
      markerList.push({
        position: currentLocation,
        title: vehicleType ? `Your Location (${getVehicleLabel(vehicleType)})` : 'Your Location',
        icon: fallbackIcon
      });
    }
    
    // For passengers, also show their own location
    if (currentLocation && user?.role === 'passenger') {
      markerList.push({
        position: currentLocation,
        title: 'Your Location',
        icon: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
      });
    }
    
    return markerList;
  }, [originCoords, destCoords, riderLocation, currentLocation, user?.role, vehicleType, vehicleIcon]);

  // Early returns after all hooks
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-600">Trip not found</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Track Your Trip</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Map */}
            <div className="lg:col-span-2">
              <div className="card">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Live Tracking</h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={trackingActive ? stopTracking : startTracking}
                      className={`btn ${trackingActive ? 'btn-danger' : 'btn-success'} text-sm`}
                    >
                      {trackingActive ? 'Stop Tracking' : 'Start Tracking'}
                    </button>
                    <button
                      onClick={shareLocation}
                      className="btn-secondary text-sm flex items-center"
                    >
                      <FaShare className="mr-1" />
                      Share
                    </button>
                  </div>
                </div>

                <MapContainer
                  origin={originCoords}
                  destination={destCoords}
                  height="500px"
                  showRoute={true}
                  markers={markers}
                  center={user?.role === 'passenger' && riderLocation ? riderLocation : (currentLocation || originCoords)}
                />

                {/* Location Status */}
                <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-800">Current Status</div>
                      <div className="text-sm text-gray-600">
                        {trip.status === 'in_progress' 
                          ? 'Trip in Progress' 
                          : trip.status === 'booked'
                          ? 'Rider on the way'
                          : 'Trip Starting Soon'}
                      </div>
                    </div>
                    <div className="text-right">
                      {user?.role === 'passenger' && riderLocation ? (
                        <>
                          <div className="text-sm font-medium text-green-600">Rider Live</div>
                          <div className="text-xs text-green-600">●</div>
                        </>
                      ) : (
                        <>
                          <div className="text-sm font-medium text-gray-800">
                            {trackingActive ? 'Live' : 'Offline'}
                          </div>
                          <div className={`text-xs ${trackingActive ? 'text-green-600' : 'text-gray-500'}`}>
                            {trackingActive ? '●' : '○'}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trip Info */}
            <div className="lg:col-span-1">
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Trip Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center mb-2">
                      <FaMapMarkerAlt className="text-green-500 mr-2" />
                      <span className="font-medium">Pickup Location</span>
                    </div>
                    <p className="text-gray-600 text-sm ml-6">{cleanAddress(trip.origin.address)}</p>
                  </div>

                  <div>
                    <div className="flex items-center mb-2">
                      <FaMapMarkerAlt className="text-red-500 mr-2" />
                      <span className="font-medium">Destination</span>
                    </div>
                    <p className="text-gray-600 text-sm ml-6">{cleanAddress(trip.destination.address)}</p>
                  </div>

                  <div>
                    <div className="flex items-center mb-2">
                      <FaClock className="mr-2" />
                      <span className="font-medium">Departure Time</span>
                    </div>
                    <p className="text-gray-600 text-sm ml-6">
                      {new Date(trip.departureTime).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center mb-2">
                      <FaMotorcycle className="mr-2" />
                      <span className="font-medium">Rider</span>
                    </div>
                    <div className="ml-6 flex items-center">
                      <img
                        src={trip.rider?.photo || '/default-avatar.png'}
                        alt={trip.rider?.name}
                        className="w-8 h-8 rounded-full mr-2"
                      />
                      <span className="text-gray-600 text-sm">{trip.rider?.name}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-2">₹{booking?.amount || trip.price}</div>
                    <p className="text-gray-600 text-sm">Trip Cost</p>
                  </div>
                </div>
              </div>

              {/* Share Trip */}
              <div className="card mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Share Trip</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Share your live location with family and friends for safety.
                </p>
                <button 
                  onClick={shareLocation}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  <FaShare className="mr-2" />
                  Share Live Location
                </button>
              </div>

              {/* Emergency */}
              <div className="card mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Emergency</h3>
                <p className="text-gray-600 text-sm mb-4">
                  In case of emergency, contact support immediately.
                </p>
                <button 
                  onClick={callEmergency}
                  className="btn-danger w-full flex items-center justify-center"
                >
                  <FaPhone className="mr-2" />
                  Emergency Contact
                </button>
              </div>

              {/* Trip Progress */}
              <div className="card mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Trip Progress</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      trip.status === 'booked' || trip.status === 'in_progress' || trip.status === 'completed' 
                        ? 'bg-green-500' : 'bg-gray-300'
                    }`}></div>
                    <span className="text-sm text-gray-600">Trip Confirmed</span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      trip.status === 'in_progress' || trip.status === 'completed' 
                        ? 'bg-green-500' : 'bg-gray-300'
                    }`}></div>
                    <span className="text-sm text-gray-600">Trip Started</span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      trip.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                    }`}></div>
                    <span className="text-sm text-gray-600">Trip Completed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripTracking;